/**
 * [비즈니스 목적]
 * 관리자용 전역 테넌트 선택 드롭다운 컴포넌트
 *
 * [워크플로우]
 * 1. GlobalTenantContext에서 테넌트 목록 및 선택 상태 가져오기
 * 2. 드롭다운에 활성 테넌트 목록 표시
 * 3. 테넌트 선택 시 전역 상태 업데이트
 * 4. 선택된 테넌트 정보를 헤더에 표시
 *
 * [주요 기능]
 * - PostgreSQL 기반 테넌트 목록 로드
 * - 멀티 테넌트 코드 지원 (tenant_codes 배열)
 * - Primary 테넌트 코드 우선 표시
 * - 활성 상태 표시 (is_active)
 * - 로딩 상태 처리
 *
 * [데이터 구조]
 * - tenant_codes: 배열 형태의 테넌트 코드 (멀티 코드 지원)
 * - is_primary: 메인 테넌트 코드 여부
 * - tenant_name: 테넌트 이름
 * - description: 테넌트 설명
 *
 * [스타일링]
 * - AppBar 내부에서 사용되므로 흰색 테마
 * - 선택 시 테넌트명(코드) 형식으로 표시
 * - 드롭다운: 아이콘 + 테넌트명 + 설명/코드 + 활성 상태 Chip
 *
 * [상태 관리]
 * - selectedTenant: 선택된 테넌트 ID
 * - tenants: 전체 테넌트 목록
 * - loading: 로딩 상태
 *
 * [사용 예시]
 * {isAdmin && <GlobalTenantSelector />}
 *
 * [참고]
 * - 관리자 권한이 있는 사용자만 표시
 * - 테넌트 선택은 localStorage에 저장되어 세션 간 유지
 */

import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { useGlobalTenant } from '../../contexts/GlobalTenantContext';

const GlobalTenantSelector = () => {
  const { selectedTenant, setSelectedTenant, tenants, loading } = useGlobalTenant();

  if (loading) {
    return (
      <Box sx={{ mr: 2 }}>
        <Typography variant="caption" color="inherit">
          로딩 중...
        </Typography>
      </Box>
    );
  }

  return (
    <FormControl 
      size="small" 
      sx={{ 
        minWidth: 200,
        mr: 2,
        '& .MuiOutlinedInput-root': {
          color: 'white',
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'white',
          },
        },
        '& .MuiInputLabel-root': {
          color: 'rgba(255, 255, 255, 0.7)',
        },
        '& .MuiSvgIcon-root': {
          color: 'white',
        },
      }}
    >
      <InputLabel id="tenant-selector-label">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <BusinessIcon fontSize="small" />
          <span>테넌트</span>
        </Box>
      </InputLabel>
      <Select
        labelId="tenant-selector-label"
        value={selectedTenant}
        onChange={(e) => setSelectedTenant(e.target.value)}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BusinessIcon fontSize="small" />
            <span>테넌트</span>
          </Box>
        }
        renderValue={(selected) => {
          // 테넌트 찾기 (PostgreSQL 필드명 사용 - tenant_codes 배열에서 검색)
          const tenant = tenants.find(t =>
            t.id === selected ||
            t.tenant_codes?.some(tc => tc.tenant_code === selected)
          );
          const tenantName = tenant?.tenant_name || tenant?.name || '알 수 없음';
          // 첫 번째 테넊트 코드 추출 (멀티 코드 지원)
          const primaryCode = tenant?.tenant_codes?.find(tc => tc.is_primary)?.tenant_code ||
                             tenant?.tenant_codes?.[0]?.tenant_code ||
                             tenant?.tenant_code ||
                             tenant?.id ||
                             selected;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="inherit">
                {tenantName} ({primaryCode})
              </Typography>
            </Box>
          );
        }}
      >
        {tenants.map((tenant) => (
          <MenuItem key={tenant.id} value={tenant.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <BusinessIcon fontSize="small" color="action" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">
                  {tenant.tenant_name || tenant.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {tenant.description || 
                   tenant.tenant_codes?.find(tc => tc.is_primary)?.tenant_code ||
                   tenant.tenant_codes?.[0]?.tenant_code ||
                   tenant.tenant_code}
                </Typography>
              </Box>
              {tenant.is_active !== false && (
                <Chip 
                  label="활성" 
                  size="small" 
                  color="success"
                  sx={{ 
                    height: 20,
                    '& .MuiChip-label': {
                      px: 1,
                      fontSize: '0.75rem'
                    }
                  }}
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default GlobalTenantSelector;